/* Shared enrichment-progress derivation for the per-realtor progress bars.
 * 10 tracked data points: 7 social platforms + email + phone + photo.
 * States: green = data on file · amber = pending manual review ·
 *         red = searched, nothing found · grey = not searched yet */

export const TRACKED_POINTS = [
  { field: "facebook_url", label: "Facebook" },
  { field: "instagram_url", label: "Instagram" },
  { field: "linkedin_url", label: "LinkedIn" },
  { field: "twitter_url", label: "X / Twitter" },
  { field: "tiktok_url", label: "TikTok" },
  { field: "youtube_url", label: "YouTube" },
  { field: "threads_url", label: "Threads" },
  { field: "email", label: "Email" },
  { field: "phone", label: "Phone" },
  { field: "photo_url", label: "Photo" },
];

const VIA_LABELS = {
  lastname_in_handle: "last name in handle",
  fullname_in_handle: "full name in handle",
  fullname_in_title: "full name in page title",
  site_query_plus_signal: "targeted platform search",
  personal_website: "personal website scan",
  legacy_match: "search match",
};

function shortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export function buildProgress({ filled, pendingFields, tracking, status }) {
  const filledSet = new Set(filled || []);
  const pendingSet = new Set(pendingFields || []);
  const t = tracking || {};
  const points = TRACKED_POINTS.map(({ field, label }) => {
    const tr = t[field];
    if (filledSet.has(field)) {
      const viaSearch = tr && tr.result === "found" && tr.source !== "backfill";
      return {
        field,
        label,
        state: "green",
        detail: viaSearch
          ? `found by search${tr.via && VIA_LABELS[tr.via] ? ` — ${VIA_LABELS[tr.via]}` : ""}`
          : "on file (import / scrape / manual)",
      };
    }
    if (pendingSet.has(field)) {
      return { field, label, state: "amber", detail: "candidate found — awaiting your review" };
    }
    if (tr && tr.searched_at) {
      return {
        field,
        label,
        state: "red",
        detail: `searched ${shortDate(tr.searched_at)} — nothing found`,
      };
    }
    return { field, label, state: "grey", detail: "not searched yet" };
  });
  const counts = { green: 0, amber: 0, red: 0, grey: 0 };
  for (const p of points) counts[p.state] += 1;
  return { points, counts, total: points.length, isEnriching: status === "pending" };
}

export function progressFromRealtor(r) {
  const filled = TRACKED_POINTS.filter(({ field }) =>
    field === "photo_url"
      ? Boolean((r.photo_url || r.photo_path || "").trim())
      : Boolean((r[field] || "").trim()),
  ).map((p) => p.field);
  const pendingFields = [
    ...new Set((r.pending_socials || []).map((p) => p.field).filter(Boolean)),
  ];
  return buildProgress({
    filled,
    pendingFields,
    tracking: r.enrichment_tracking,
    status: r.enrichment_status,
  });
}

export function progressFromSlim(p) {
  return buildProgress({
    filled: p.filled,
    pendingFields: p.pending_fields,
    tracking: p.tracking,
    status: p.enrichment_status,
  });
}
